import React, { FC, useContext, useEffect, useMemo, useState } from 'react';

import {
  Button,
  CheckIcon,
  H2,
  Input,
  Modal,
  PlusIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext } from 'localization';

import s from './ColorPicker.module.scss';
import { COLORS } from './constants';
import CustomColor from './CustomColor';
import { IColorPickerProps } from './interfaces';

export const ColorPicker: FC<IColorPickerProps> = ({
  setColor,
  color,
  disabled,
  colors = COLORS,
  variant = 'primary',
}) => {
  const { t } = useContext(LocalizationContext);

  const setColors = new Set(colors);
  const [isOpen, setIsOpen] = useState(false);
  const [curColor, setCurColor] = useState<string>(color);
  const [isValid, setIsValid] = useState<boolean>(true);

  const handleColorChange = (value: string): void => {
    setCurColor(value);
    setIsValid(/^#[0-9A-F]{6}$/i.test(value));
  };

  useEffect(() => {
    setCurColor(color);
  }, [color]);

  const isCustom = useMemo<boolean>(() => !setColors.has(color), [color]);

  return (
    <div className={s.container}>
      {isOpen &&
        (variant === 'primary' ? (
          <Modal className={s.modal} onClose={() => setIsOpen(false)}>
            <H2 className={s.modal__title}>{t('colorPicker.title')}</H2>
            <CustomColor
              color={color}
              setColor={setColor}
              variant={variant}
              close={() => setIsOpen(false)}
            />
          </Modal>
        ) : (
          <div className={s.modal_secondary}>
            <div className={s.modal_secondary__header}>
              <H2 className={s.modal__title}>
                {t('colorPicker.title.secondary')}
              </H2>
              <Button
                onlyIcon
                iconName="CloseIcon"
                variant="ghost"
                className={s.modal_secondary__close}
                onClick={() => setIsOpen(false)}
              />
            </div>

            <CustomColor
              color={color}
              setColor={setColor}
              variant={variant}
              close={() => setIsOpen(false)}
            />
          </div>
        ))}
      <div className={cn(s.colors, disabled && s.disabled)}>
        {colors.map((colorItem) => (
          <button
            className={s.color}
            key={colorItem}
            onClick={() => {
              setColor(colorItem);
            }}
          >
            {colorItem === color && (
              <CheckIcon className={s.check} color="light" size="m" />
            )}
            <div
              className={s.color__item}
              style={{ backgroundColor: colorItem }}
            />
          </button>
        ))}
        <button
          className={cn(s.color, s.color__custom, isCustom && s.selected)}
          onClick={() => setIsOpen(true)}
        >
          <PlusIcon
            color={isCustom ? 'light' : 'primary'}
            className={cn(s.icon, s.icon__add)}
          />
          <CheckIcon color="light" className={cn(s.icon, s.icon__check)} />

          <div
            className={s.color__item}
            style={{ backgroundColor: isCustom ? color : 'transparent' }}
          />
        </button>
      </div>
      {variant === 'primary' && (
        <Input
          value={curColor}
          onChange={handleColorChange}
          type="text"
          title={t('hex_code.title')}
          bordered
          iconName="PaletteIcon"
          className={cn(s.input, disabled && s.disabled)}
          error={!isValid ? t('hex_code.error') : undefined}
          onBlur={() => {
            if (isValid) {
              setColor(curColor);
            } else {
              setCurColor(color);
              setIsValid(true);
            }
          }}
          errorWrapperClassName={s.input_block}
        />
      )}
    </div>
  );
};
